import { useQuery } from "@apollo/client";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { gql } from "generated-graphql";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useMemo } from "react";

import { mergeDefaultAndOmnisearch } from "./mergeDefaultAndOmnisearch";

const PROCESSED_MANIFESTS_STATS_QUERY = gql(`
  query ProcessedManifestsStats($search: String) {
    processedManifestsStats(search: $search) {
      totalCount
      averageDaysAwaitingEManifest
      averageDiscrepancies
    }
  }
`);

function formatDays(value: number | null | undefined): string {
  if (value == null) return "—";
  // Whole-day rounding feels right for an average with no decimals shown.
  // The exact value is a tooltip away if precision matters.
  const rounded = Math.round(value);
  return rounded === 1 ? "1 day" : `${rounded} days`;
}

function formatDiscrepancyAvg(value: number | null | undefined): string {
  if (value == null) return "—";
  // 1 decimal is enough resolution to communicate "is it < 1 / about 1 / many".
  return value.toFixed(1);
}

export interface ManifestStatsCardsProps {
  /** Omnisearch string the grid is currently filtered by. */
  search: string;
}

/**
 * Three compact summary cards rendered above the manifests grid.
 *
 * The cards re-issue a lightweight aggregate query whenever the omnisearch
 * `search` changes, so they stay in lock-step with the grid. Apollo's default
 * cache key keeps repeated identical searches from hammering the backend.
 *
 * The aggregate runs on the server through the same omnisearch pipeline
 * `processedManifests` uses (see `processedManifestsStats` resolver). This
 * means filters like `hasSource:eManifest` or `unresolvedDiscrepancyCount:>0`
 * are honored without the frontend duplicating the predicate logic.
 */
export function ManifestStatsCards({ search }: ManifestStatsCardsProps) {
  const { data, loading } = useQuery(PROCESSED_MANIFESTS_STATS_QUERY, {
    variables: { search },
    // Stats can lag behind the grid query without consequence — `cache-and-network`
    // gives users an instantaneous first paint with a background refresh.
    fetchPolicy: "cache-and-network",
  });

  const cards = useMemo(() => {
    const stats = data?.processedManifestsStats;
    return [
      {
        label: "Manifests",
        value: stats?.totalCount?.toLocaleString() ?? "—",
      },
      {
        label: "Avg time awaiting e-Manifest",
        value: formatDays(stats?.averageDaysAwaitingEManifest),
      },
      {
        label: "Avg discrepancies",
        value: formatDiscrepancyAvg(stats?.averageDiscrepancies),
      },
    ];
  }, [data]);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ width: "100%" }}
      role="group"
      aria-label="Manifest summary"
    >
      {cards.map((card) => (
        <StatCard key={card.label} {...card} loading={loading && !data} />
      ))}
    </Stack>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  loading: boolean;
}

/**
 * Convenience wrapper that pulls the active omnisearch string from context and
 * merges it with `defaultSearch` (the page-level scope, e.g. tenantId + year)
 * before handing it to the stats cards. Mirrors the merge that
 * `OmnisearchDataDisplay` does internally so the cards stay in lock-step with
 * the grid without prop-drilling `search` through every render.
 *
 * Smart-merge rules: a key in `omnisearch` overrides the same key in
 * `defaultSearch` (preventing duplicate filters from arriving at the backend
 * as `tenantId:abc tenantId:abc`).
 */
export function ManifestStatsCardsConnected({ defaultSearch }: { defaultSearch: string }) {
  const { omnisearch } = useOmnisearch();
  const search = useMemo(
    () => mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
    [defaultSearch, omnisearch]
  );
  return <ManifestStatsCards search={search} />;
}

function StatCard({ label, value, loading }: StatCardProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        // Pale gray fill, no outline — the boxes read as a quiet stat strip
        // sitting on the darker gray page canvas, not as bordered cards.
        backgroundColor:
          theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[800],
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: "none",
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" sx={{ width: "60%", height: 28 }} />
      ) : (
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}
